import React from "react";
import { Box, Typography, Avatar } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { alpha } from "@mui/material/styles";
import { stringAvatar } from "../../config/AvatarColor";
import { formatMessageTime } from "../../utils/timestampUtils";

const MEDIA_ICONS = {
  image: ImageIcon,
  video: VideocamIcon,
  audio: AudiotrackIcon,
  document: InsertDriveFileIcon,
};

function LastMessagePreview({ lastMessage, lastMessageType }) {
  const MediaIcon = MEDIA_ICONS[lastMessageType];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, minWidth: 0 }}>
      {MediaIcon && (
        <MediaIcon sx={{ fontSize: "0.85rem", color: "text.secondary", flexShrink: 0 }} />
      )}
      <Typography
        variant="body2"
        noWrap
        sx={{
          color: "text.secondary",
          fontSize: { xs: "0.78rem", sm: "0.75rem", md: "0.78rem" },
          lineHeight: 1.3,
          minWidth: 0,
        }}
      >
        {lastMessage || "No messages yet"}
      </Typography>
    </Box>
  );
}

export default function TaskChatCard({ item, selected, onClick }) {
  const {
    taskName = "Untitled",
    lastMessage,
    lastMessageAt,
    lastMessageType = "text",
    unreadCount = 0,
  } = item;

  const avatarProps = stringAvatar(taskName);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        padding: { xs: "4px 6px", sm: "3px 5px", md: "5px 8px", lg: "6px 10px", xl: "7px 11px" },
        minHeight: { xs: "70px", sm: "60px", md: "70px", lg: "70px", xl: "70px" },
        cursor: "pointer",
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: selected
          ? (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1)
          : "transparent",
        "&:hover": {
          bgcolor: selected
            ? (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.13)
            : (theme) => theme.palette.action.hover,
        },
        transition: "background-color 0.15s",
      }}
    >
      {/* Avatar */}
      <Avatar
        {...avatarProps}
        sx={{
          ...avatarProps.sx,
          width: { xs: 40, sm: 32, md: 40, lg: 40, xl: 40 },
          height: { xs: 40, sm: 32, md: 40, lg: 40, xl: 40 },
          fontSize: { xs: "16px", sm: "12px", md: "16px", lg: "16px", xl: "16px" },
          flexShrink: 0,
        }}
      />

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.2 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: unreadCount > 0 ? 700 : 600,
              fontSize: { xs: "0.88rem", sm: "0.85rem", md: "0.88rem" },
              color: "text.primary",
              flex: 1,
              minWidth: 0,
              pr: 0.5,
            }}
          >
            {taskName}
          </Typography>

          {lastMessageAt && (
            <Typography
              variant="caption"
              sx={{
                color: unreadCount > 0 ? "primary.main" : "text.secondary",
                fontSize: { xs: "0.68rem", sm: "0.65rem", md: "0.68rem" },
                whiteSpace: "nowrap",
                flexShrink: 0,
                fontWeight: unreadCount > 0 ? 600 : 400,
              }}
            >
              {formatMessageTime(lastMessageAt)}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
            <LastMessagePreview lastMessage={lastMessage} lastMessageType={lastMessageType} />
          </Box>

          {unreadCount > 0 && (
            <Box
              sx={{
                bgcolor: "primary.main",
                color: "#fff",
                borderRadius: "50%",
                minWidth: 22,
                height: 22,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.8rem",
                fontWeight: 700,
                flexShrink: 0,
                ml: 0.5,
              }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
